import { isolate } from '@batiflow/ui';
import { __css, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { listOf, summarize, type StepMode } from './approvals-model';

const useStyles = __css({"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9"}});

export interface StepsSummaryProps {
  steps: readonly {
    mode: StepMode;
    reviewerIds: readonly string[];
    reviewerOrgIds?: readonly string[];
  }[];
  /** Name of a reviewer by user id. */
  nameOf: (userId: string) => string;
  /** Name of a partner office by organisation id (null or missing when unknown). */
  officeNameOf?: (orgId: string) => string | null | undefined;
  label?: string;
}

/**
 * "Who decides when" in plain words: one line per step, naming its reviewers and whether they
 * decide in that order or in any order. Steps with no reviewer yet are left out.
 */
export function StepsSummary({ steps, nameOf, officeNameOf, label }: StepsSummaryProps) {
  const { t } = useTranslation('documents');
  const locale = useLocale();
  const styles = useStyles();
  const officeOf = (orgId: string) =>
    t('approvals.summary.officeApprovers', {
      office: isolate(officeNameOf?.(orgId) ?? t('approvals.stepper.unknownOffice')),
    });
  const lines = summarize(steps, nameOf, officeOf).filter((line) => line.names.length > 0);
  if (lines.length === 0) return null;
  return (
    <ul className={styles.list} {...(label ? { 'aria-label': label } : {})}>
      {lines.map((line) => (
        <li key={line.ordinal}>
          {t(`approvals.summary.${line.mode}`, {
            n: line.ordinal,
            names: listOf(
              locale,
              line.names.map((name) => isolate(name)),
            ),
          })}
        </li>
      ))}
    </ul>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/approvals/steps-summary.griffel.css";